import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiChevronLeft, FiChevronRight } = FiIcons

const SignatureExamples = () => {
  // Extract examples from the images (excluding Business-Name)
  const examples = [
    {
      src: "/examples/Professional-Signature-Example-Amira Thornton-signature.jpg",
      name: "Amira Thornton"
    },
    {
      src: "/examples/Professional-Signature-Example-Chad Mills-signature.jpg",
      name: "Chad Mills"
    },
    {
      src: "/examples/Professional-Signature-Example-Daniel O'Keefe-signature.jpg",
      name: "Daniel O'Keefe"
    },
    {
      src: "/examples/Professional-Signature-Example-Julia Nakamura-signature.jpg",
      name: "Julia Nakamura"
    },
    {
      src: "/examples/Professional-Signature-Example-Leila Chen-signature.jpg",
      name: "Leila Chen"
    },
    {
      src: "/examples/Professional-Signature-Example-Lena Hargrave-signature.jpg",
      name: "Lena Hargrave"
    },
    {
      src: "/examples/Professional-Signature-Example-Marcus Avery-signature.jpg",
      name: "Marcus Avery"
    },
    {
      src: "/examples/Professional-Signature-Example-Rachel Vasquez-signature.jpg",
      name: "Rachel Vasquez"
    }
  ]

  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)

  // Auto-advance carousel
  useEffect(() => {
    if (!isAutoPlaying) return

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === examples.length - 1 ? 0 : prevIndex + 1
      )
    }, 4000) // Change every 4 seconds

    return () => clearInterval(interval)
  }, [isAutoPlaying, examples.length])

  const goToPrevious = () => {
    setIsAutoPlaying(false)
    setCurrentIndex(currentIndex === 0 ? examples.length - 1 : currentIndex - 1)
  }

  const goToNext = () => {
    setIsAutoPlaying(false)
    setCurrentIndex(currentIndex === examples.length - 1 ? 0 : currentIndex + 1)
  }

  const goToSlide = (index) => {
    setIsAutoPlaying(false)
    setCurrentIndex(index)
  }

  // Resume auto-play after user interaction
  useEffect(() => {
    if (!isAutoPlaying) {
      const timeout = setTimeout(() => {
        setIsAutoPlaying(true)
      }, 8000) // Resume after 8 seconds of no interaction

      return () => clearTimeout(timeout)
    }
  }, [isAutoPlaying])

  return (
    <section id="examples" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
            Signature Examples
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            See real signatures created by our AI for professionals like you
          </p>
        </motion.div>

        <div className="relative max-w-4xl mx-auto">
          {/* Main carousel container */}
          <div className="relative overflow-hidden rounded-2xl bg-gray-50 shadow-xl">
            {/* Carousel wrapper */}
            <div className="relative h-64 md:h-80 lg:h-96">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  className="absolute inset-0 flex items-center justify-center p-8"
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                >
                  <div className="flex flex-col items-center justify-center space-y-6 w-full">
                    {/* Name */}
                    <h3 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold text-gray-900 text-center">
                      {examples[currentIndex].name}
                    </h3>
                    
                    {/* Signature Image */}
                    <div className="flex-shrink-0">
                      <img
                        src={examples[currentIndex].src}
                        alt={`Professional signature for ${examples[currentIndex].name}`}
                        className="max-w-full h-auto max-h-40 md:max-h-52 lg:max-h-64 object-contain shadow-lg rounded-lg bg-white p-6"
                        onError={(e) => {
                          console.error('Image failed to load:', e.target.src)
                          e.target.style.display = 'none'
                        }}
                      />
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation arrows */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-700 p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
              aria-label="Previous signature"
            >
              <SafeIcon icon={FiChevronLeft} className="text-xl" />
            </button>
            
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-700 p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
              aria-label="Next signature"
            >
              <SafeIcon icon={FiChevronRight} className="text-xl" />
            </button>
          </div>

          {/* Dots indicator */}
          <div className="flex justify-center mt-8 space-x-2">
            {examples.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  index === currentIndex
                    ? 'bg-primary-600 scale-125'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to signature ${index + 1}`}
              />
            ))}
          </div>

          {/* Auto-play indicator */}
          <div className="text-center mt-4">
            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              {isAutoPlaying ? 'Pause slideshow' : 'Resume slideshow'}
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default SignatureExamples
